import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Images, Rocket, Play, X, ChevronRight, ChevronLeft, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { mediaUrl } from "@/lib/download";
import { Watermarked } from "@/components/Watermarked";
import { track } from "@/lib/funnel";

export const useShowcase = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/public/showcase").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  return items;
};

/** معاينة كبيرة (lightbox) لصورة أو فيديو مع تنقّل بين العينات */
const Lightbox = ({ items, index, onClose, onMove }) => {
  const it = items[index];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") onMove(1); if (e.key === "ArrowRight") onMove(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onMove]);
  if (!it) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sample-lightbox">
      <button className="absolute top-[max(1rem,env(safe-area-inset-top))] end-4 z-10 h-12 w-12 rounded-full bg-white text-black shadow-lg ring-2 ring-black/30 hover:bg-neutral-200 flex items-center justify-center" onClick={onClose} aria-label="إغلاق" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      {items.length > 1 && (
        <>
          <button className="absolute start-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/90 text-black shadow-lg hover:bg-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onMove(-1); }} aria-label="السابق" data-testid="lightbox-prev"><ChevronRight className="w-6 h-6" /></button>
          <button className="absolute end-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/90 text-black shadow-lg hover:bg-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onMove(1); }} aria-label="التالي" data-testid="lightbox-next"><ChevronLeft className="w-6 h-6" /></button>
        </>
      )}
      <div className="max-w-[92vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <Watermarked className="rounded-xl">
        {it.type === "video"
          ? <video src={mediaUrl(it.url)} className="max-w-[92vw] max-h-[85vh] rounded-xl" controls controlsList="nodownload" disablePictureInPicture autoPlay playsInline data-testid="lightbox-video" />
          : <img src={mediaUrl(it.url)} alt="" draggable={false} className="max-w-[92vw] max-h-[85vh] rounded-xl object-contain" data-testid="lightbox-image" />}
        </Watermarked>
      </div>
      <div className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-1 text-xs pointer-events-none">
        <div className="rounded-full bg-black/70 text-white font-bold px-4 py-1.5" data-testid="lightbox-caption">عينة من منصة رواج الذكية لأحد عملائنا</div>
        <div className="text-white/70">{index + 1} / {items.length} · اضغط خارج الصورة أو × للإغلاق</div>
      </div>
    </div>
  );
};

/** شبكة عينات قابلة للمعاينة — تُستخدم في الرئيسية (6) وفي صفحة مكتبة العينات (الكل) */
export const SampleGrid = ({ items, limit }) => {
  const [open, setOpen] = useState(-1);
  const shown = limit ? items.slice(0, limit) : items;
  const move = (d) => setOpen((i) => (i + d + shown.length) % shown.length);
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {shown.map((it, i) => (
          <button type="button" key={it.id} onClick={() => setOpen(i)} onContextMenu={(e) => e.preventDefault()} className="group relative rounded-2xl overflow-hidden border border-border bg-card aspect-square text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-primary select-none" data-testid={`showcase-item-${it.id}`} aria-label="معاينة العينة">
            <Watermarked className="w-full h-full" label={false}>
            {it.type === "video"
              ? <video src={mediaUrl(it.url)} className="w-full h-full object-cover" muted playsInline loop autoPlay preload="metadata" />
              : <img src={mediaUrl(it.url)} alt="" loading="lazy" draggable={false} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
            </Watermarked>
            <span className="absolute top-2 start-2 rounded-full bg-black/60 text-white text-[11px] px-2 py-0.5 flex items-center gap-1">
              {it.type === "video" ? <><Play className="w-3 h-3" /> فيديو</> : <><ImageIcon className="w-3 h-3" /> صورة</>}
            </span>
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-white/90 text-foreground text-xs font-bold px-3 py-1">معاينة</span>
            </span>
          </button>
        ))}
      </div>
      {open >= 0 && <Lightbox items={shown} index={open} onClose={() => setOpen(-1)} onMove={move} />}
    </>
  );
};

/** عينات من منصة رواج الذكية لعملائها — ليرى الزائر الجودة قبل التسجيل */
export const Showcase = () => {
  const items = useShowcase();
  if (!items || items.length === 0) return null;
  return (
    <section id="showcase" className="py-20 px-6 max-w-7xl mx-auto" data-testid="showcase-section">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 text-primary text-sm font-bold mb-3"><Images className="w-4 h-4" /> من إنتاج المنصة فعلًا</div>
        <h2 className="text-base md:text-lg font-bold">صور وفيديوهات أنتجها رواج الذكية بالكامل بالذكاء الاصطناعي — سعودية الملامح، جاهزة للنشر</h2>
        <p className="text-xs text-muted-foreground mt-2">اضغط أي عينة لمعاينتها بالحجم الكامل</p>
      </div>
      <SampleGrid items={items} limit={6} />
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
        {items.length > 6 && (
          <Link to="/samples">
            <Button size="lg" variant="outline" className="gap-2 font-bold h-12 px-8 border-border" data-testid="showcase-all"><Images className="w-5 h-5" /> مكتبة العينات ({items.length})</Button>
          </Link>
        )}
        <Link to="/register" onClick={() => track("cta_click", "showcase")}>
          <Button size="lg" className="gap-2 font-bold h-12 px-8" data-testid="showcase-cta"><Rocket className="w-5 h-5" /> أنتج مثلها لمشروعك مجانًا</Button>
        </Link>
      </div>
    </section>
  );
};
